// Routes through Fly proxy (same as osuApi.ts) to sidestep CORS on osu!'s no-auth room endpoint.

import { isUpstreamUnavailable, OsuUnavailableError } from './osuApi.js';

const API_BASE = 'https://proxy.replayviewer.com/osu-proxy';

export function parseRoomRef(input: string): string | null {
  // Strip zero-width/BOM/bidi marks alongside whitespace; pasted URLs frequently retain them.
  const s = input.replace(/[\s​-‏﻿]/g, '');
  if (s === '') return null;
  if (/^\d+$/.test(s)) return s;
  const m = /\/multiplayer\/rooms\/(\d+)/i.exec(s);
  return m !== null ? m[1]! : null;
}

export interface MatchScore {
  // Lazer-namespace score ID usable with downloadReplay.
  scoreId: string;
  userId: number;
  username: string;
  accuracy: number;
  pp: number | null;
  rank: string;
  mods: string[];
  hasReplay: boolean;
  // score-v2 total on lazer scores; drives per-map winner + match aggregates.
  totalScore: number | null;
}

export type TeamColor = 'red' | 'blue';

export interface MatchPlaylistItem {
  playlistItemId: number;
  beatmapId: number;
  beatmapsetId: number;
  title: string;
  artist: string;
  version: string;
  beatmapMd5: string;
  coverUrl: string | null;
  scores: MatchScore[];
  // Non-empty for team_versus; empty Map for head_to_head.
  teams: Map<number, TeamColor>;
}

export interface MatchPlayer {
  userId: number;
  username: string;
  mapWins: number;
  avgAccuracy: number;
  totalScore: number;
  // null for non-team rooms or users never seen in a team assignment.
  team: TeamColor | null;
}

// Only team_versus triggers the team-match UI; everything else falls under 'other'.
export type RoomType = 'team_versus' | 'head_to_head' | 'other';

export interface MatchData {
  roomId: number;
  name: string;
  roomType: RoomType;
  playlistItems: MatchPlaylistItem[];
  // Deduped users sorted: team rank, then mapWins desc, then username asc — pins canvas order.
  players: MatchPlayer[];
}

interface RawUser   { id: number; username: string }
interface RawBeatmap {
  id: number;
  beatmapset_id: number;
  version: string;
  checksum: string;
}
interface RawCovers  { 'cover@2x'?: string; cover?: string }
interface RawBeatmapset {
  id: number;
  title: string;
  artist: string;
  covers?: RawCovers;
}
interface RawMod { acronym: string }
interface RawScore {
  id?: number;
  solo_score_id?: number;
  user_id: number;
  accuracy: number;
  pp: number | null;
  rank: string;
  mods: RawMod[];
  has_replay?: boolean;
  total_score?: number;
}
// teams is null (not absent) on non-team rooms.
interface RawPlaylistItemDetails {
  teams?: Record<string, TeamColor> | null;
  room_type?: string;
}
interface RawPlaylistItem {
  id: number;
  beatmap_id: number;
  scores?: RawScore[];
  details?: RawPlaylistItemDetails;
}
interface RawRoom { id: number; name: string; type?: string }
interface RawResponse {
  room: RawRoom;
  beatmaps: RawBeatmap[];
  beatmapsets: RawBeatmapset[];
  users: RawUser[];
  playlist_items: RawPlaylistItem[];
}

export async function fetchRoom(roomId: string): Promise<MatchData> {
  const url = `${API_BASE}/multiplayer/rooms/${roomId}/events`;
  const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!resp.ok) {
    // No-auth endpoint, so no AuthError here — only outage classification.
    if (isUpstreamUnavailable(resp)) throw new OsuUnavailableError();
    throw new Error(`room fetch failed for ${roomId} (HTTP ${resp.status})`);
  }
  const raw = await resp.json() as RawResponse;

  const userById = new Map<number, RawUser>();
  for (const u of raw.users) userById.set(u.id, u);

  const beatmapById = new Map<number, RawBeatmap>();
  for (const b of raw.beatmaps) beatmapById.set(b.id, b);

  const beatmapsetById = new Map<number, RawBeatmapset>();
  for (const s of raw.beatmapsets) beatmapsetById.set(s.id, s);

  const playlistItems: MatchPlaylistItem[] = [];
  for (const item of raw.playlist_items) {
    const beatmap = beatmapById.get(item.beatmap_id);
    if (beatmap === undefined) continue;
    const beatmapset = beatmapsetById.get(beatmap.beatmapset_id);

    const scores: MatchScore[] = [];
    for (const s of item.scores ?? []) {
      const scoreId = s.solo_score_id ?? s.id;
      if (scoreId === undefined) continue;
      const u = userById.get(s.user_id);
      scores.push({
        scoreId:    String(scoreId),
        userId:     s.user_id,
        username:   u?.username ?? `user ${s.user_id}`,
        accuracy:   s.accuracy,
        pp:         s.pp,
        rank:       s.rank,
        mods:       s.mods.map(m => m.acronym),
        hasReplay:  s.has_replay ?? false,
        totalScore: s.total_score ?? null,
      });
    }

    // osu! returns teams: null on non-team rooms rather than omitting it.
    const teams = new Map<number, TeamColor>();
    const rawTeams = item.details?.teams;
    if (rawTeams != null) {
      for (const [uidStr, color] of Object.entries(rawTeams)) {
        const uid = Number(uidStr);
        if (!Number.isFinite(uid)) continue;
        if (color === 'red' || color === 'blue') teams.set(uid, color);
      }
    }

    playlistItems.push({
      playlistItemId: item.id,
      beatmapId:      beatmap.id,
      beatmapsetId:   beatmap.beatmapset_id,
      title:          beatmapset?.title  ?? '',
      artist:         beatmapset?.artist ?? '',
      version:        beatmap.version,
      beatmapMd5:     beatmap.checksum,
      coverUrl:       beatmapset?.covers?.['cover@2x'] ?? beatmapset?.covers?.cover ?? null,
      scores,
      teams,
    });
  }

  // osu! returns playlist_items newest-first; id is monotonic, so ascending = play order.
  playlistItems.sort((a, b) => a.playlistItemId - b.playlistItemId);

  const rawRoomType = raw.room.type;
  const roomType: RoomType = rawRoomType === 'team_versus'
    ? 'team_versus'
    : rawRoomType === 'head_to_head' ? 'head_to_head' : 'other';

  return {
    roomId: raw.room.id,
    name:   raw.room.name,
    roomType,
    playlistItems,
    players: summarizePlayers(playlistItems),
  };
}

// Per-map winners credited to mapWins: team rooms credit all winners by team total; ties → no credit.
function summarizePlayers(items: MatchPlaylistItem[]): MatchPlayer[] {
  interface Agg {
    userId: number;
    username: string;
    mapWins: number;
    accSum:  number;
    scoreCount: number;
    totalScore: number;
    team: TeamColor | null;
  }
  const byId = new Map<number, Agg>();
  for (const item of items) {
    const isTeamMap = item.teams.size > 0;

    let winnerUserIds = new Set<number>();
    if (isTeamMap) {
      const red  = item.scores.filter(s => item.teams.get(s.userId) === 'red');
      const blue = item.scores.filter(s => item.teams.get(s.userId) === 'blue');
      const sumOf = (ss: MatchScore[]) => ss.reduce((n, s) => n + (s.totalScore ?? 0), 0);
      const redSum  = sumOf(red);
      const blueSum = sumOf(blue);
      if (red.length > 0 && blue.length > 0 && redSum !== blueSum) {
        const winners = redSum > blueSum ? red : blue;
        for (const s of winners) winnerUserIds.add(s.userId);
      }
    } else if (item.scores.length >= 2) {
      const withScore = item.scores.filter(s => s.totalScore !== null);
      if (withScore.length > 0) {
        const top = withScore.reduce((best, s) => s.totalScore! > best.totalScore! ? s : best);
        const topVal = top.totalScore!;
        const topCount = withScore.filter(s => s.totalScore === topVal).length;
        if (topCount === 1) winnerUserIds.add(top.userId);
      }
    }

    for (const s of item.scores) {
      let agg = byId.get(s.userId);
      if (agg === undefined) {
        agg = {
          userId: s.userId, username: s.username,
          mapWins: 0, accSum: 0, scoreCount: 0, totalScore: 0,
          team: null,
        };
        byId.set(s.userId, agg);
      }
      agg.accSum     += s.accuracy;
      agg.scoreCount += 1;
      agg.totalScore += s.totalScore ?? 0;
      if (winnerUserIds.has(s.userId)) agg.mapWins += 1;
      if (agg.team === null) {
        const t = item.teams.get(s.userId);
        if (t !== undefined) agg.team = t;
      }
    }
  }
  return Array.from(byId.values())
    .map(a => ({
      userId:      a.userId,
      username:    a.username,
      mapWins:     a.mapWins,
      avgAccuracy: a.scoreCount > 0 ? a.accSum / a.scoreCount : 0,
      totalScore:  a.totalScore,
      team:        a.team,
    }))
    .sort((a, b) => {
      const rank = (t: TeamColor | null) => t === 'red' ? 0 : t === 'blue' ? 1 : 2;
      const d = rank(a.team) - rank(b.team);
      if (d !== 0) return d;
      return b.mapWins - a.mapWins || a.username.localeCompare(b.username);
    });
}
